import type { components } from '@adiox/api';

export type TradingReport = components['schemas']['TradingReport'];
type TradeStats = components['schemas']['TradeStats'];

export interface ReportLine {
  label: string;
  value: string;
  /** Colour the figure by its sign. */
  tone?: 'up' | 'down';
  /** A short explanation for the reader. */
  hint?: string;
}

export interface ReportSection {
  title: string;
  lines: ReportLine[];
}

const pct = (v: number) => `${v.toFixed(2)}%`;
const toneOf = (v: number): ReportLine['tone'] => (v > 0 ? 'up' : v < 0 ? 'down' : undefined);
const share = (part: number, whole: number) =>
  whole === 0 ? '—' : `${part} (${((100 * part) / whole).toFixed(2)}%)`;

/** MT5's report as sections of labelled figures: the same lines are shown and exported. */
export function reportSections(r: TradingReport, money: (v: number) => string): ReportSection[] {
  const a: TradeStats = r.all;
  return [
    {
      title: 'Results',
      lines: [
        { label: 'Total net profit', value: money(a.netProfit), tone: toneOf(a.netProfit) },
        { label: 'Gross profit', value: money(a.grossProfit) },
        { label: 'Gross loss', value: money(a.grossLoss) },
        {
          label: 'Profit factor',
          value: a.profitFactor === null || a.profitFactor === undefined ? '—' : a.profitFactor.toFixed(2),
          hint: 'Gross profit divided by gross loss; above 1 means the winners outweigh the losers.',
        },
        {
          label: 'Expected payoff',
          value: money(a.expectedPayoff),
          tone: toneOf(a.expectedPayoff),
          hint: 'The average result of one trade.',
        },
        {
          label: 'Recovery factor',
          value:
            r.recoveryFactor === null || r.recoveryFactor === undefined ? '—' : r.recoveryFactor.toFixed(2),
          hint: 'Net profit divided by the maximal drawdown.',
        },
        { label: 'Deposits', value: money(r.deposits) },
        { label: 'Withdrawals', value: money(r.withdrawals) },
        { label: 'Balance at the start', value: money(r.initialBalance) },
        { label: 'Balance at the end', value: money(r.finalBalance) },
      ],
    },
    {
      title: 'Drawdown',
      lines: [
        {
          label: 'Balance drawdown absolute',
          value: money(r.absoluteDrawdown),
          hint: 'How far the balance went below where it started.',
        },
        {
          label: 'Balance drawdown maximal',
          value: `${money(r.maximalDrawdown.money)} (${pct(r.maximalDrawdown.percent)})`,
          hint: 'The largest fall from a peak, in money.',
        },
        {
          label: 'Balance drawdown relative',
          value: `${pct(r.relativeDrawdown.percent)} (${money(r.relativeDrawdown.money)})`,
          hint: 'The largest fall from a peak, in percent.',
        },
        {
          label: 'Equity drawdown maximal',
          value:
            r.equityDays > 0
              ? `${money(r.equityMaximalDrawdown.money)} (${pct(r.equityMaximalDrawdown.percent)})`
              : '—',
          hint: 'Measured on the end-of-day equity, including open positions.',
        },
      ],
    },
    {
      title: 'Trades',
      lines: [
        { label: 'Total trades', value: String(a.trades) },
        { label: 'Win rate', value: pct(a.winRate) },
        { label: 'Profit trades', value: share(a.wins, a.trades) },
        { label: 'Loss trades', value: share(a.losses, a.trades) },
        { label: 'Long trades (won)', value: `${r.longs.trades} (${pct(r.longs.winRate)})` },
        { label: 'Short trades (won)', value: `${r.shorts.trades} (${pct(r.shorts.winRate)})` },
        { label: 'Largest profit trade', value: money(r.largestProfit), tone: toneOf(r.largestProfit) },
        { label: 'Largest loss trade', value: money(r.largestLoss), tone: toneOf(r.largestLoss) },
        { label: 'Average profit trade', value: money(r.averageProfit) },
        { label: 'Average loss trade', value: money(r.averageLoss) },
        { label: 'Volume traded', value: `${a.volume} lots` },
      ],
    },
    {
      title: 'Streaks',
      lines: [
        {
          label: 'Most consecutive wins',
          value: `${r.maxConsecutiveWins.count} (${money(r.maxConsecutiveWins.money)})`,
        },
        {
          label: 'Most consecutive losses',
          value: `${r.maxConsecutiveLosses.count} (${money(r.maxConsecutiveLosses.money)})`,
        },
        {
          label: 'Largest winning streak',
          value: `${money(r.maxConsecutiveProfit.money)} (${r.maxConsecutiveProfit.count})`,
        },
        {
          label: 'Largest losing streak',
          value: `${money(r.maxConsecutiveLoss.money)} (${r.maxConsecutiveLoss.count})`,
        },
      ],
    },
  ];
}
